// Headless "restricted combobox" behaviour shared by every skin that renders a
// searchable, dependent select (lo-preview's SearchableSelect, the Artemis
// console's ComboField). Typing filters the supplied options, but only an
// explicit click or Enter commits a value; DOM focus stays in the input and
// aria-activedescendant exposes the highlighted listbox option (WAI-ARIA APG
// "editable combobox with list autocomplete, manual selection").
//
// This module deliberately imports nothing but React so it can live in the
// lo-preview bundle (a separate Vite entry) without dragging main-app
// dependencies along. Skins own all markup, classes and tokens.
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject
} from 'react';

export interface RestrictedComboboxOption {
  label: string;
  value: string;
}

export interface UseRestrictedComboboxArgs {
  value: string;
  onChange: (value: string) => void;
  options: readonly RestrictedComboboxOption[];
  disabled?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /**
   * When the committed `value` matches no option (a record prefilled with a
   * county outside the catalog), show that raw value as the input text instead
   * of an empty box so the user can see what is stored before replacing it.
   * Off by default: lo-preview only ever commits catalog values.
   */
  showUnmatchedValue?: boolean;
}

export interface RestrictedComboboxInputProps {
  role: 'combobox';
  'aria-autocomplete': 'list';
  'aria-expanded': boolean;
  'aria-controls': string;
  'aria-activedescendant'?: string;
  autoComplete: 'off';
  spellCheck: false;
  value: string;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus: (event: FocusEvent<HTMLInputElement>) => void;
  onClick: (event: MouseEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export interface RestrictedComboboxListboxProps {
  id: string;
  role: 'listbox';
}

export interface RestrictedComboboxOptionProps {
  id: string;
  role: 'option';
  'aria-selected': boolean;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  onMouseEnter: () => void;
}

export interface RestrictedCombobox {
  open: boolean;
  query: string;
  activeIndex: number;
  selectedOption: RestrictedComboboxOption | undefined;
  filteredOptions: readonly RestrictedComboboxOption[];
  listboxId: string;
  optionId: (index: number) => string;
  /** Outside-pointer containment for the in-flow part of the skin. */
  rootRef: RefObject<HTMLElement>;
  /** Optional second containment root for a listbox rendered through a portal. */
  panelRef: RefObject<HTMLElement>;
  getInputProps: () => RestrictedComboboxInputProps;
  getListboxProps: () => RestrictedComboboxListboxProps;
  getOptionProps: (option: RestrictedComboboxOption, index: number) => RestrictedComboboxOptionProps;
  isActive: (index: number) => boolean;
  isSelected: (option: RestrictedComboboxOption) => boolean;
}

export function useRestrictedCombobox({
  value,
  onChange,
  options,
  disabled,
  onEditingChange,
  showUnmatchedValue = false
}: UseRestrictedComboboxArgs): RestrictedCombobox {
  const id = useId();
  const listboxId = `searchable-select-${id}`;
  const rootRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );
  // The text the input shows while nothing is being typed: the committed
  // option's label, or (console prefill only) the raw unmatched value.
  const committedDisplayValue = selectedOption?.label ?? (showUnmatchedValue ? value : '');
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const committedLabel = committedDisplayValue.toLocaleLowerCase();
    if (!normalizedQuery || normalizedQuery === committedLabel) {
      return options;
    }
    return options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery));
  }, [committedDisplayValue, options, query]);
  const activeDescendantId = open && filteredOptions[activeIndex]
    ? `${listboxId}-option-${activeIndex}`
    : undefined;

  useEffect(() => {
    if (!open) {
      setQuery(committedDisplayValue);
    }
  }, [committedDisplayValue, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveIndex((index) => (
      filteredOptions.length ? Math.min(Math.max(index, 0), filteredOptions.length - 1) : -1
    ));
  }, [filteredOptions, open]);

  useEffect(() => {
    function closeOnOutsidePointer(event: globalThis.PointerEvent) {
      const target = event.target as Node;
      const insideRoot = Boolean(rootRef.current?.contains(target));
      const insidePanel = Boolean(panelRef.current?.contains(target));
      if (open && rootRef.current && !insideRoot && !insidePanel) {
        setOpen(false);
        setQuery(committedDisplayValue);
        onEditingChange?.(false);
      }
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [committedDisplayValue, onEditingChange, open]);

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId, open]);

  function optionId(index: number) {
    return `${listboxId}-option-${index}`;
  }

  function openList() {
    if (disabled) {
      return;
    }
    setOpen(true);
    const selectedIndex = filteredOptions.findIndex((option) => option.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : filteredOptions.length ? 0 : -1);
  }

  function selectCommittedLabel(input: HTMLInputElement) {
    if (committedDisplayValue && query === committedDisplayValue) {
      input.select();
    }
  }

  function closeList() {
    setOpen(false);
    setActiveIndex(-1);
    setQuery(committedDisplayValue);
    onEditingChange?.(false);
  }

  function choose(option: RestrictedComboboxOption) {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
    setActiveIndex(-1);
    onEditingChange?.(false);
  }

  function handleChange(nextQuery: string) {
    onEditingChange?.(true);
    if (value) {
      onChange('');
    }
    setQuery(nextQuery);
    setOpen(true);
    setActiveIndex(options.length ? 0 : -1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (disabled) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setActiveIndex((index) => filteredOptions.length ? Math.max(index - 1, 0) : -1);
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = filteredOptions[activeIndex];
      if (option) {
        choose(option);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      closeList();
      return;
    }
    if (event.key === 'Tab') {
      closeList();
    }
  }

  function getInputProps(): RestrictedComboboxInputProps {
    return {
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': open,
      'aria-controls': listboxId,
      'aria-activedescendant': activeDescendantId,
      autoComplete: 'off',
      spellCheck: false,
      value: query,
      disabled,
      onChange: (event) => handleChange(event.target.value),
      onFocus: (event) => {
        openList();
        selectCommittedLabel(event.currentTarget);
      },
      onClick: (event) => {
        openList();
        selectCommittedLabel(event.currentTarget);
      },
      onKeyDown: handleKeyDown
    };
  }

  function getListboxProps(): RestrictedComboboxListboxProps {
    return { id: listboxId, role: 'listbox' };
  }

  function getOptionProps(option: RestrictedComboboxOption, index: number): RestrictedComboboxOptionProps {
    return {
      id: optionId(index),
      role: 'option',
      'aria-selected': option.value === value,
      onPointerDown: (event) => {
        // Keep mouse focus in the combobox without turning the start of a
        // touch-scroll gesture into an accidental selection.
        if (event.pointerType === 'mouse') {
          event.preventDefault();
        }
      },
      onClick: (event) => {
        // A skin may wrap the field in a <label>; cancel its implicit input
        // activation so selecting an option cannot immediately reopen the
        // listbox after choose() closes it (notably on touch).
        event.preventDefault();
        event.stopPropagation();
        choose(option);
      },
      onMouseEnter: () => setActiveIndex(index)
    };
  }

  return {
    open,
    query,
    activeIndex,
    selectedOption,
    filteredOptions,
    listboxId,
    optionId,
    rootRef,
    panelRef,
    getInputProps,
    getListboxProps,
    getOptionProps,
    isActive: (index) => index === activeIndex,
    isSelected: (option) => option.value === value
  };
}
